import React, { useState, useEffect } from "react";
import axios from "axios";
import Menu from "../Menu";
import { useNavigate, useParams } from "react-router-dom";

const SubGroupData = () => {
  const { subgroupId, groupId } = useParams();
  const [svgFile, setSvgFile] = useState(null);
  const [fileType, setFileType] = useState(""); // "svg" or "csv"
  const [extractedData, setExtractedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // success or error

  const navigate = useNavigate();
  const token = getCookie("token");
  const userid = getCookie("userid");

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type === "image/svg+xml") {
        setSvgFile(file);
        setFileType("svg");
        setMessage("");
      } else if (file.type === "text/csv" || file.name.endsWith(".csv")) {
        setSvgFile(file);
        setFileType("csv");
        setMessage("");
      } else {
        setMessage("Please select a valid SVG or CSV file");
        setMessageType("error");
        setSvgFile(null);
        setFileType("");
      }
    }
  };

  const extractCSVData = async () => {
    try {
      const text = await svgFile.text();
      const lines = text.trim().split("\n");
      
      if (lines.length < 2) {
        setMessage("CSV file appears to be empty");
        setMessageType("error");
        return;
      }

      const data = [];
      
      // Process each line (skip header if present)
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue; // Skip empty lines

        // Parse CSV line (basic parsing, handles quoted values)
        const values = line.split(",").map((val) => val.trim().replace(/^"|"$/g, ""));
        
        if (values.length >= 2) {
          data.push({
            merchantid: values[0] || "",
            posname: values[1] || "",
            status: "", // Initially blank
            msg: "", // Initially blank
            remark: "", // Initially blank
            active: true,
            apiResponse: null, // Store full API response
          });
        }
      }

      if (data.length === 0) {
        setMessage("No valid records found in CSV");
        setMessageType("error");
        return;
      }

      setExtractedData(data);
      setMessage(`CSV data extracted successfully! ${data.length} records found.`);
      setMessageType("success");
    } catch (error) {
      console.error("Error extracting CSV:", error);
      setMessage("Error extracting CSV data. Please check the file format.");
      setMessageType("error");
    }
  };

  const extractSVGData = async () => {
    if (!svgFile) {
      setMessage("Please select an SVG file first");
      setMessageType("error");
      return;
    }

    setLoading(true);
    try {
      const text = await svgFile.text();
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(text, "image/svg+xml");

      // Extract text elements from SVG
      const textElements = svgDoc.querySelectorAll("text, tspan");
      const data = [];
      let posName = "";
      let merchantName = "";
      let status = "";
      let msg = "";

      // Group consecutive text elements into records
      let currentRecord = {};
      let recordIndex = 0;
      let skipCount = 0; // Skip first 2 header elements (Merchant Name, Pos Name)

      textElements.forEach((element, index) => {
        const textContent = element.textContent.trim();
        if (textContent) {
          // Skip the first 2 header elements (header row)
          if (skipCount < 2) {
            skipCount++;
            return;
          }

          // Extract Merchant Name first, then POS Name (according to SVG column order)
          if (recordIndex === 0) {
            currentRecord.merchantid = textContent;
            recordIndex++;
          } else if (recordIndex === 1) {
            currentRecord.posname = textContent;
            // Complete record with blank status and message
            data.push({
              ...currentRecord,
              status: "", // Initially blank
              msg: "", // Initially blank
              remark: "", // Initially blank
              active: true,
              apiResponse: null, // Store full API response
            });
            currentRecord = {};
            recordIndex = 0;
          }
        }
      });

      if (data.length === 0) {
        // Alternative: Try to extract from tspan elements with x and y positioning
        const tspans = svgDoc.querySelectorAll("tspan");
        let extractedRecords = [];
        let currentData = {};
        let skipHeaderCount = 0; // Skip first 2 header elements

        tspans.forEach((tspan) => {
          const text = tspan.textContent.trim();
          if (text) {
            // Skip the first 2 header elements (header row)
            if (skipHeaderCount < 2) {
              skipHeaderCount++;
              return;
            }

            if (!currentData.merchantid) {
              currentData.merchantid = text;
            } else if (!currentData.posname) {
              currentData.posname = text;
              // Complete record with blank status and message
              extractedRecords.push({
                ...currentData,
                status: "", // Initially blank
                msg: "", // Initially blank
                remark: "", // Initially blank
                active: true,
                apiResponse: null, // Store full API response
              });
              currentData = {};
            }
          }
        });

        setExtractedData(extractedRecords);
      } else {
        setExtractedData(data);
      }

      setMessage("SVG data extracted successfully!");
      setMessageType("success");
    } catch (error) {
      console.error("Error extracting SVG:", error);
      setMessage("Error extracting SVG data. Please check the file format.");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  const handleExtractData = async () => {
    setLoading(true);
    try {
      if (fileType === "csv") {
        await extractCSVData();
      } else if (fileType === "svg") {
        await extractSVGData();
      } else {
        setMessage("Unknown file type");
        setMessageType("error");
      }
    } finally {
      setLoading(false);
    }
  };

  const submitDataToAPI = async () => {
    if (extractedData.length === 0) {
      setMessage("No data to submit");
      setMessageType("error");
      return;
    }

    setUploading(true);
    let successCount = 0;
    let errorCount = 0;
    const updatedData = [...extractedData];

    try {
      for (let i = 0; i < extractedData.length; i++) {
        const record = extractedData[i];
        try {
          const payload = {
            subgroupid: parseInt(subgroupId),
            posname: record.posname || "",
            merchantid: record.merchantid || "",
            remark: record.remark || record.msg || "",
            active: record.active || true,
          };

          const response = await axios.post(
            "https://api.dqr-display-tms.bonrix.in/api/SubGroupDataOperation/SubUser/Insert",
            payload,
            {
              headers: {
                "X-Authorization": token,
                UserId: userid,
                "Content-Type": "application/json-patch+json",
                accept: "text/plain",
              },
            }
          );

          // Update Status and Message/Remark with API response
          updatedData[i].apiResponse = response.data; // Store full API response
          updatedData[i].status = "Success";
          updatedData[i].msg = response.data?.message || "Record submitted successfully";
          updatedData[i].remark = response.data?.message || "Record submitted successfully";

          // Update the state to reflect changes one by one
          setExtractedData([...updatedData]);

          successCount++;
        } catch (error) {
          console.error(`Error submitting record ${i + 1}:`, error);
          
          // Update Status and Message/Remark with error information
          updatedData[i].apiResponse = error.response?.data; // Store full API response (error)
          updatedData[i].status = "Failed";
          updatedData[i].msg = error.response?.data?.errormsg || error.response?.data?.message || error.message || "Error submitting record";
          updatedData[i].remark = error.response?.data?.errormsg || error.response?.data?.message || error.message || "Error submitting record";

          // Update the state to reflect changes one by one
          setExtractedData([...updatedData]);

          errorCount++;
        }
      }

      setMessage(
        `Data submitted successfully! Success: ${successCount}, Failed: ${errorCount}`
      );
      setMessageType("success");

      // Reset form after successful submission
      setTimeout(() => {
        navigate(`/subgroup/${groupId}`);
      }, 20000);
    } catch (error) {
      console.error("Error submitting data:", error);
      setMessage("Error submitting data to API");
      setMessageType("error");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <Menu />
      <div className="wrapper">
        <div className="content-wrapper">
          <section className="content-header">
            <div className="container-fluid">
              <div className="d-flex justify-content-between align-items-center w-100">
                <div>
                  <h1 className="mb-0">Upload Data CSV</h1>
                  {/* <p className="text-muted">SubGroup: <strong>{subgroupName}</strong></p> */}
                </div>
                <div>
                 
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate(`/subgroup/${subgroupId}`)}
                  >
                    <i className="fas fa-arrow-left mr-1"></i>Back to SubGroup
                  </button>
                </div>
              </div>
            </div>
          </section>

          <section className="content">
            <div className="container-fluid">
              <div className="row">
                <div className="col-lg-8">
                  <div className="card">
                    <div className="card-header">
                      <h3 className="card-title">File Upload CSV</h3>
                    </div>
                    <div className="card-body">
                      <div className="form-group">
                        <label htmlFor="svgFile">Select File:</label>
                        <input
                          type="file"
                          className="form-control"
                          id="svgFile"
                          accept=".svg,.csv"
                          onChange={handleFileChange}
                          disabled={loading || uploading}
                        />
                        <small className="form-text text-muted">
                          Upload  CSV file. CSV should have columns: Merchant ID, POS Name
                        </small>
                      </div>

                      <div className="form-group">
                        <button
                          className="btn btn-info"
                          onClick={handleExtractData}
                          disabled={!svgFile || loading || uploading}
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm mr-2"></span>
                              Extracting...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-file-import mr-2"></i>
                              Extract Data
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Extracted Data Table */}
              {extractedData.length > 0 && (
                <div className="row mt-3">
                  <div className="col-12">
                    <div className="card">
                      <div className="card-header">
                        <h3 className="card-title">
                          Extracted Data ({extractedData.length} records)
                        </h3>
                      </div>
                      <div className="card-body table-responsive p-0">
                        <table className="table table-hover table-striped table-sm">
                          <thead className="thead-light">
                            <tr>
                              <th style={{ width: "3%" }} className="text-center">
                                #
                              </th>
                              <th style={{ width: "15%" }}>POS Name</th>
                              <th style={{ width: "15%" }}>Merchant ID</th>
                              <th style={{ width: "10%" }}>Status</th>
                              <th style={{ width: "57%" }}>API Response / Message</th>
                            </tr>
                          </thead>
                          <tbody>
                            {extractedData.map((record, index) => (
                              <tr key={index}>
                                <td className="text-center">{index + 1}</td>
                                <td>{record.posname || "-"}</td>
                                <td>{record.merchantid || "-"}</td>
                                <td>
                                  <span
                                    className={`badge ${
                                      record.status === "Success"
                                        ? "badge-success"
                                        : record.status === "Failed"
                                        ? "badge-danger"
                                        : "badge-secondary"
                                    }`}
                                  >
                                    {record.status || "Pending"}
                                  </span>
                                </td>
                                <td>
                                  <div style={{ fontSize: "20px", lineHeight: "1.4" }}>
                                    {record.apiResponse ? (
                                      <div>
                                        <br />
                                        {/* <strong>Message:</strong>{" "} */}
                                        {record.apiResponse?.errormsg ||
                                          record.apiResponse?.message ||
                                          record.msg ||
                                          "-"}
                                       
                                      </div>
                                    ) : (
                                      record.msg || record.remark || "-"
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="card-footer">
                        <button
                          className="btn btn-success"
                          onClick={submitDataToAPI}
                          disabled={uploading}
                        >
                          {uploading ? (
                            <>
                              <span className="spinner-border spinner-border-sm mr-2"></span>
                              Submitting...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-cloud-upload-alt mr-2"></i>
                              Submit Data to Database
                            </>
                          )}
                        </button>
                        <button
                          className="btn btn-secondary ml-2"
                          onClick={() => {
                            setExtractedData([]);
                            setSvgFile(null);
                            document.getElementById("svgFile").value = "";
                          }}
                          disabled={uploading}
                        >
                          <i className="fas fa-redo mr-2"></i>
                          Reset
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      {/* Message display */}
      {message && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            right: "10px",
            width: "25%",
            zIndex: "9999",
          }}
        >
          <div
            className={`alert ${
              messageType === "success" ? "alert-success" : "alert-danger"
            } d-flex align-items-center`}
            role="alert"
            style={{ marginBottom: "10px" }}
          >
            <div>
              {messageType === "success" ? (
                <i className="fas fa-check-circle mr-2"></i>
              ) : (
                <i className="fas fa-exclamation-circle mr-2"></i>
              )}
              {message}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SubGroupData;

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}

